/* eslint-disable max-statements */

import { Button, Flex, Modal, Stack, Text, Title } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';

import { DocumentsEnum } from '@e-origin/shared';

import { deleteIcon, viewIcon } from '@assets';
import { Table, TableActions, TableColumnProps, TableConditionalRowStyles } from '@components';
import { DocumentEdit, documentsLabels } from './DocumentEdit';
import { DocumentFormSchemaProps } from './DocumentFormFactory';

interface DocumentsProps {
  title?: string;
  disabledDocuments?: DocumentsEnum[];
  disabled: boolean;
  previewDocuments?: Record<string, any[]>;
  documents: Record<string, any[]>;
  onSave: (savedDocument: any) => void;
  onDelete: (documentType: any, documentIndex: number) => void;
  documentFormSchema: DocumentFormSchemaProps;
}

export interface DocumentItemProps {
  type: DocumentsEnum;
  label?: string;
  code: string;
  referenceNumber?: string;
  complementOfInformation?: string;
  dateOfValidity?: string;
  docDetails: {
    type: DocumentsEnum;
    dataSource: any;
    dataSourceIndex: number;
  };
  readonly: boolean;
}

export const Documents: React.FC<DocumentsProps> = ({
  title = '',
  disabledDocuments = [],
  disabled,
  previewDocuments = {},
  documents = {},
  onSave,
  onDelete,
  documentFormSchema,
}) => {
  const [data, setData] = useState<DocumentItemProps[]>([]);
  const selectedRow = useRef<DocumentItemProps>(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const toggleShowDocumentModal = () => {
    setShowModal(!showModal);

    if (showModal) {
      selectedRow.current = null;
    }
  };

  const toggleShowDeleteDocumentModal = () => {
    setShowDeleteModal(!showDeleteModal);

    if (showDeleteModal) {
      selectedRow.current = null;
    }
  };

  const handleSavingDocument = (savedDocument: any) => {
    onSave(savedDocument);
    selectedRow.current = null;
  };

  const buildRow = (documentType: DocumentsEnum, item: any, itemIdx: number, isPreview = false) => ({
    type: documentType,
    label: documentsLabels[documentType],
    code: item.type || item.code || item.role || '',
    complementOfInformation: item.complementOfInformation || '',
    referenceNumber: item.referenceNumber || item.text || item.vatIdentificationNumber || item.reference?.customsOfficeGuarantee?.referenceNumber || '',
    docDetails: { type: documentType, dataSource: item, dataSourceIndex: itemIdx },
    readonly: isPreview,
  });

  useEffect(() => {
    const tableData: DocumentItemProps[] = [];

    for (const [documentType, docs] of Object.entries(previewDocuments || [])) {
      docs.forEach((item, itemIdx) => tableData.push(buildRow(documentType as DocumentsEnum, item, itemIdx, true)));
    }

    for (const [documentType, docs] of Object.entries(documents || [])) {
      docs.forEach((item, itemIdx) =>
        tableData.push(buildRow(documentType as DocumentsEnum, item, itemIdx, disabledDocuments.includes(documentType as DocumentsEnum))),
      );
    }

    setData(tableData);
  }, [documents]);

  const columns: TableColumnProps<DocumentItemProps>[] = [
    {
      name: 'Type of document',
      cell: (row) => row.label,
    },
    {
      name: 'Code',
      cell: (row) => row.code,
    },
    {
      name: 'Reference',
      cell: (row) => row.referenceNumber,
      width: '250px',
    },
    {
      name: 'Complement of information',
      cell: (row) => row.complementOfInformation,
      width: '250px',
    },
    {
      name: 'Actions',
      sticky: 'right',
      cell: (row: DocumentItemProps) => (
        <TableActions
          actions={[
            {
              icon: viewIcon,
              text: 'View details',
              onClick: () => {
                setShowModal(true);
              },
            },
            {
              if: !row.readonly && !disabled,
              icon: deleteIcon,
              text: 'Delete',
              onClick: () => {
                setShowDeleteModal(true);
              },
            },
          ]}
          onClick={() => {
            selectedRow.current = row;
          }}
        />
      ),
    },
  ];

  const conditionalRowStyles: TableConditionalRowStyles<DocumentItemProps> = {
    when: (row) => row.readonly,
    style: {
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
    },
  };

  return (
    <Stack style={{ flexShrink: 0 }}>
      <Flex justify={title.length > 0 ? 'space-between' : 'flex-end'} align="center" my={title.length > 0 || !disabled ? 'xl' : ''}>
        {title.length > 0 && (
          <Title order={5} fw={600}>
            {title}
          </Title>
        )}

        {!disabled && (
          <Button onClick={toggleShowDocumentModal} variant="outline" size="lg" data-pw="add-document-btn">
            Add new
          </Button>
        )}
      </Flex>

      <Table columns={columns} dataSource={data || []} conditionalRowStyles={conditionalRowStyles} pagination verticalSpacing="xs" />

      {showModal && (
        <DocumentEdit
          opened={true}
          onHide={toggleShowDocumentModal}
          docDetails={selectedRow.current?.docDetails}
          onSave={handleSavingDocument}
          editDisabled={disabled || selectedRow.current?.readonly}
          disabledDocuments={disabledDocuments}
          documentFormSchema={documentFormSchema}
        />
      )}
      {showDeleteModal && (
        <Modal title="Confirm document delete" opened={showDeleteModal} onClose={toggleShowDeleteDocumentModal} centered zIndex={9999}>
          <Text>Are you sure you want to delete this document?</Text>
          <Button
            color="red"
            mt="md"
            onClick={() => {
              onDelete(selectedRow.current.type, selectedRow.current.docDetails.dataSourceIndex);
              toggleShowDeleteDocumentModal();
            }}
          >
            Delete
          </Button>
        </Modal>
      )}
    </Stack>
  );
};
